import { z } from "zod";
import { getShouldHaveBlindVertical } from "../data";
import { DRILLING_HOLE_DIAMETER } from "../parts/Shelf";
import { IdPrefix, generateId } from "../utils";
import { BACKGROUND_THICKNESS } from "./Background";
import type { CompartmentType } from "./Compartment";
import { DOOR_THICKNESS } from "./Door";

export const ShelfSchema = z.object({
	id: z.string(),
	x: z.number(),
	y: z.number(),
	z: z.number(),
	width: z.number(),
	height: z.number(),
	depth: z.number(),
});

export type Shelf = z.infer<typeof ShelfSchema>;

const SHELF_THICKNESS = 18;

export function createShelves(
	compartment: CompartmentType,
	count: number,
	thickness: number,
): Shelf[] {
	const holePositions = getShelfHoleYPositions(compartment);
	const hasBlindVertical = getShouldHaveBlindVertical(compartment);

	if (hasBlindVertical) {
		const maxAmount = getMaxShelfAmount(compartment) * 2;
		const amount = Math.min(Math.ceil(count / 2), maxAmount);

		return Array.from({ length: amount }, (_, index) => {
			const holePosition = holePositions[index] ?? 0;

			return [
				{
					id: generateId(IdPrefix.Shelf),
					x: compartment.x + compartment.width / 2 + thickness / 2,
					y: (holePosition + DRILLING_HOLE_DIAMETER) * 1000,
					z: compartment.z + 10 + BACKGROUND_THICKNESS / 2,
					width: compartment.width / 2 - thickness / 2,
					height: SHELF_THICKNESS,
					depth: compartment.depth - DOOR_THICKNESS - 7 - 10 - BACKGROUND_THICKNESS,
				},
				{
					id: generateId(IdPrefix.Shelf),
					x: compartment.x,
					y: (holePosition + DRILLING_HOLE_DIAMETER) * 1000,
					z: compartment.z + 10 + BACKGROUND_THICKNESS / 2,
					width: compartment.width / 2 - thickness / 2,
					height: SHELF_THICKNESS,
					depth: compartment.depth - DOOR_THICKNESS - 7 - 10 - BACKGROUND_THICKNESS,
				},
			];
		}).flat();
	}

	// make sure we don't create more shelves than we can fit
	const maxAmount = getMaxShelfAmount(compartment);
	const amount = Math.min(count, maxAmount);

	return Array.from({ length: amount }, (_, index) => {
		const holePosition = holePositions[index] ?? 0;

		return {
			id: generateId(IdPrefix.Shelf),
			x: compartment.x,
			y: (holePosition + DRILLING_HOLE_DIAMETER) * 1000,
			z: compartment.z + 10 + BACKGROUND_THICKNESS / 2,
			width: compartment.width,
			height: SHELF_THICKNESS,
			depth: compartment.depth - DOOR_THICKNESS - 7 - 10,
		};
	});
}

const INSERT_HOLE_Y_OFFSET = 200;
const DRILL_OFFSET = 32;

export function getMaxShelfAmount(compartment: CompartmentType) {
	const maxAmount = Math.ceil(compartment.height - 2 * INSERT_HOLE_Y_OFFSET) / DRILL_OFFSET;
	const count = Math.round(Math.max(3, maxAmount));

	// make sure we have an odd number of insert holes
	return count % 2 === 0 ? count + 1 : count;
}

export function getShelfHoleYPositions(compartment: CompartmentType) {
	const maxAmount = getMaxShelfAmount(compartment);

	return Array.from({ length: maxAmount }).map((_, i) => {
		const y = compartment.height / 2 - Math.floor(maxAmount / 2) * DRILL_OFFSET + i * DRILL_OFFSET;

		return (compartment.y + y) / 1000;
	});
}
